<script setup>
  import { computed } from "vue";
  import { storeToRefs } from "pinia";
  import { Select } from "@/components/episto-ui/select";
  import { Input } from "@/components/ui/input";
  import { Button } from "@/components/ui/button";
  import { Trash2, Plus } from "lucide-vue-next";
  import { getRegionOptions } from "@utils/constants/regions";
  import { createDefaultCountry } from "@utils/constants/pricingDefaults";
  import { formatInputValue, parseInputValue } from "@utils/formatters";
  import { calculateCountryTotalDisplay } from "@utils/pricer/pricerHelpers";
  import { usePricerProjectStore } from "@stores/pricer/pricerProject";

  const projectStore = usePricerProjectStore();
  const { selectedTarget: target } = storeToRefs(projectStore);

  const availableCountries = getRegionOptions();

  const columns = [
    { name: "country", label: "Country", type: "country-select", width: "col-span-5", align: "left" },
    { name: "n", label: "N", type: "number", width: "col-span-2", placeholder: "N", align: "left" },
    { name: "cpi_auto", label: "Auto CPI", type: "readonly", width: "col-span-2", suffix: "€", align: "left" },
    { name: "cpi_manual", label: "Manual CPI", type: "number", width: "col-span-2", align: "left" },
    { name: "total", label: "Total", type: "calculated", width: "col-span-3", suffix: "€", align: "left" },
    { name: "delete", type: "delete", width: "col-span-1", align: "left" },
    { name: "country_auto", type: "readonly", width: "w-20", suffix: "", align: "right" },
    { name: "country_manual", type: "number", width: "w-20", align: "right" },
  ];

  const mainColumns = computed(() => columns.filter((col) => col.align === "left"));
  const sideColumns = computed(() => columns.filter((col) => col.align === "right"));

  const addCountry = () => {
    if (!target.value.countries) target.value.countries = [];
    target.value.countries.push(createDefaultCountry(target.value.id));
  };

  const removeCountry = (index) => target.value.countries.splice(index, 1);
</script>

<template>
  <div class="space-y-4">
    <div class="flex gap-12 text-13 font-medium text-foreground mb-2">
      <div class="grid grid-cols-15 gap-4 flex-1">
        <div
          v-for="col in mainColumns"
          :key="col.name"
          :class="[col.width, 'whitespace-nowrap']">
          {{ col.label }}
        </div>
      </div>
      <div class="flex gap-4">
        <div
          v-for="col in sideColumns"
          :key="col.name"
          :class="[col.width, 'whitespace-nowrap']">
          {{ col.label }}
        </div>
      </div>
    </div>

    <div
      v-for="(countryData, index) in target.countries"
      :key="index"
      class="flex gap-12 mb-4">
      <div class="grid grid-cols-15 gap-4 flex-1">
        <div
          v-for="col in mainColumns"
          :key="col.name"
          :class="[col.width, 'flex items-center']">
          <Select
            v-if="col.type === 'country-select'"
            v-model="countryData[col.name]"
            class="w-full"
            :options="availableCountries"
            placeholder="Select country"
            filterable />
          <Input
            v-if="col.type === 'number'"
            :class="col.align === 'center' ? 'text-center' : ''"
            :model-value="formatInputValue(countryData[col.name])"
            :placeholder="col.placeholder"
            @update:model-value="countryData[col.name] = parseInputValue($event)" />
          <div
            v-else-if="col.type === 'calculated'"
            class="font-bold">
            {{ calculateCountryTotalDisplay(countryData) }} {{ col.suffix }}
          </div>
          <Input
            v-if="col.type === 'readonly'"
            v-model="countryData[col.name]"
            :suffix="col.suffix"
            class="text-center text-muted-foreground border-dashed border-1"
            readonly />
          <Button
            v-else-if="col.type === 'delete'"
            variant="ghost"
            size="icon-sm"
            :disabled="target.countries.length === 1"
            @click="removeCountry(index)">
            <Trash2 />
          </Button>
        </div>
      </div>
      <div class="flex items-center gap-4">
        <div
          v-for="col in sideColumns"
          :key="col.name"
          :class="col.width">
          <Input
            v-if="col.type === 'readonly'"
            v-model="countryData[col.name]"
            class="text-center text-muted-foreground border-dashed border-1"
            readonly />
          <Input
            v-else-if="col.type === 'number'"
            class="text-center"
            :model-value="formatInputValue(countryData[col.name])"
            @update:model-value="countryData[col.name] = parseInputValue($event)" />
          <div
            v-else-if="col.type === 'calculated'"
            class="text-center font-bold">
            {{ calculateCountryTotalDisplay(countryData) }} {{ col.suffix }}
          </div>
        </div>
      </div>
    </div>
    <Button
      class="mt-2 w-fit"
      variant="outline"
      @click="addCountry">
      <Plus />
      Add country
    </Button>
  </div>
</template>
